import { useWeightUnit } from '@/hooks/useWeightUnit';
import { ABC_COLORS, NO_ABC_COLOR, supplierColor } from '@/modules/supply/utils/loadColors';
import { ProductConfigStrip, type ProductConfigPayload } from './ProductConfigStrip';
import { PalletFrontGallery, PalletFrontGraphic, palletFrontView, type PalletFrontSlot } from './PalletFrontGallery';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Info, Lock, Maximize2, Minimize2, Search, Ship, Truck, Unlock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import type { SupplyAgentLogisticsCalculateResponse } from '@/services/supplyAgentService';
import { SupplyIsometricLoadView } from './SupplyIsometricLoadView';
import { MOTION_STYLES, useCountUp, useGrowIn } from './visualMotion';
import { blocksFromPlacements, DEFAULT_ISO_DIMS, planPallets, synthesizeBlocks } from './isometricLoad';
import { TablePaginationBar, DEFAULT_TABLE_PAGE_SIZE, TABLE_PAGE_SIZE_OPTIONS } from '@/components/shared/TablePaginationBar';

// Datos de la ficha del producto (maestro de productos); el resto de campos faltantes son del pallet por proveedor.
const PRODUCT_CARD_FIELDS = new Set(['units_per_box', 'effective_unit_volume_m3', 'effective_unit_weight_kg', 'temperature_zone']);

/** Cambio de unidades de «Completar hasta objetivo» para un producto (original → propuesto o aplicado). */
export interface LoadTargetAdjustment {
  originalUnits: number;
  newUnits: number;
  coverageBefore?: number | null;
  coverageAfter?: number | null;
  /** Nombre y SKU para mostrar productos que entran nuevos al ampliar surtido (no están en la estimación). */
  label?: string;
  sku?: string;
}

/** Columna «Ajuste objetivo»: propuesta pendiente (con aceptar/rechazar por línea) o ajuste ya aplicado. */
export interface LoadTargetColumn {
  state: 'proposal' | 'applied';
  byProduct: Record<number, LoadTargetAdjustment>;
  accepted?: Record<number, boolean>;
  onToggle?: (productId: number, accepted: boolean) => void;
}

/** Contexto comercial del producto en la tabla de supply. */
export interface LoadProductContext {
  stock?: number | null;
  inTransit?: number | null;
  monthlySales?: number | null;
  estimatedMonthlySales?: number | null;
  coverageDays?: number | null;
  coverageWithSuggestedDays?: number | null;
  abcCategory?: string | null;
  lowCoverage?: boolean;
}

interface SupplyLoadVisualizationProps {
  result: SupplyAgentLogisticsCalculateResponse;
  t: (key: string, options?: Record<string, unknown>) => string;
  estimateOnly?: boolean;
  showSupplier?: boolean;
  extraBoxesByProduct?: Record<string, number>;
  onExtraBoxesChange?: (next: Record<string, number>) => void;
  onApplyRoundedBoxes?: (items: Array<{ productId: number; supplierId: number | null; quantity: number }>) => void;
  onClearRoundedBoxes?: (items: Array<{ productId: number; supplierId: number | null }>) => void;
  targetColumn?: LoadTargetColumn | null;
  productContext?: Record<number, LoadProductContext>;
  /** Líneas bloqueadas: el ajuste del contenedor no las cambia. */
  lockedProducts?: Record<number, boolean>;
  onToggleLock?: (productId: number, locked: boolean) => void;
  /** Guarda el packaging del producto en el maestro y recalcula la estimación. */
  onSaveProductConfig?: (payload: ProductConfigPayload) => Promise<void>;
  /** Acciones finales del pedido (p. ej. crear órdenes), junto a la lista de productos. */
  productsAction?: ReactNode;
  /** Módulo de estimación: solo el camión en 2D y la ocupación; sin 3D, selectores de vista, explicaciones ni detalle de pallets. */
  compact?: boolean;
  /** Con compact: porcentaje de ocupación que llena el camión (el de la restricción limitante), el mismo que el panel de ocupación. */
  fillPct?: number | null;
  /** Con compact: oculta las tarjetas de volumen, pallets y peso (p. ej. con un filtro activo, donde no representan el subconjunto). */
  hideMetrics?: boolean;
  /** Con compact: colorea el relleno del camión por clasificación ABC o por proveedor. */
  colorMode?: 'abc' | 'supplier';
  /** Con compact y colorMode: qué porcentaje del contenedor ocupa cada grupo (clave ABC o id de proveedor), para que
   *  quien llama lo muestre igual que en el relleno (p. ej. en una leyenda). */
  onGroupFillPct?: (pct: Record<string, number>) => void;
}

function productColor(index: number): string {
  const hue = Math.round((index * 137.508 + 198) % 360);
  return 'hsl(' + hue + ' 68% 42%)';
}

function formatProductVolume(value: number): string {
  return value.toLocaleString(undefined, { maximumSignificantDigits: 4 });
}

function formatLoadQuantity(value: number, maximumFractionDigits = 2): string {
  return value.toLocaleString(undefined, { maximumFractionDigits });
}

function numeric(value: unknown): number | null {
  const parsed = Number(value);
  return value != null && Number.isFinite(parsed) ? parsed : null;
}

function percent(value: number | null): number | null {
  return value == null ? null : Math.max(0, Math.min(100, value));
}

function fillLimit(value: unknown): number | null {
  const parsed = numeric(value);
  if (parsed == null) return null;
  return parsed <= 1 ? parsed * 100 : parsed;
}

function wrapPalletLabel(label: string, maxChars: number): string[] {
  const words = label.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars || !current) {
      current = candidate;
      return;
    }
    lines.push(current);
    current = word;
  });
  if (current) lines.push(current);
  if (lines.length > 2) {
    const second = lines.slice(1).join(' ');
    lines.splice(1, lines.length - 1, second.slice(0, Math.max(1, maxChars - 1)).trimEnd() + '…');
  }
  return lines;
}

function MetricBar({
  label,
  value,
  unit,
  minimum,
  maximum,
  barClass = 'bg-gradient-to-r from-sky-400 to-sky-600',
}: {
  label: string;
  value: number | null;
  unit: string;
  minimum?: number | null;
  maximum?: number | null;
  barClass?: string;
}) {
  const bounded = percent(value);
  const min = percent(minimum ?? null);
  const max = percent(maximum ?? null);
  const outside = value != null && ((minimum != null && value < minimum) || (maximum != null && value > maximum));
  const grown = useGrowIn(bounded);
  const shown = useCountUp(value);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
        <span className="font-medium text-slate-700">{label}</span>
        <span className={`text-lg font-bold tabular-nums leading-none ${outside ? 'text-amber-700' : 'text-slate-800'}`}>
          {shown == null ? '—' : `${shown.toLocaleString(undefined, { maximumFractionDigits: 1 })}${unit}`}
        </span>
      </div>
      <div className="relative h-2.5 overflow-visible rounded-full bg-slate-200 shadow-inner">
        <div
          className={`sv-fill h-full rounded-full ${outside ? 'bg-gradient-to-r from-amber-400 to-amber-500' : barClass}`}
          style={{ width: `${grown}%` }}
        />
        {min != null ? <span className="absolute -top-0.5 h-3 w-px bg-indigo-600" style={{ left: `${min}%` }} /> : null}
        {max != null ? <span className="absolute -top-0.5 h-3 w-px bg-rose-600" style={{ left: `${max}%` }} /> : null}
      </div>
    </div>
  );
}

function UnitsDeltaChip({ delta }: { delta: number }) {
  if (Math.abs(delta) < 0.001) return <span className="inline-block rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">±0</span>;
  const up = delta > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${up ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
      <span aria-hidden="true">{up ? '▲' : '▼'}</span>
      {up ? '+' : '−'}{formatLoadQuantity(Math.abs(delta), 0)}
    </span>
  );
}

export function SupplyLoadVisualization({ result, t, estimateOnly = false, showSupplier = false, extraBoxesByProduct: controlledExtraBoxes, onExtraBoxesChange, onApplyRoundedBoxes, onClearRoundedBoxes, targetColumn = null, productContext, lockedProducts, onToggleLock, onSaveProductConfig, productsAction, compact = false, fillPct = null, hideMetrics = false, colorMode, onGroupFillPct }: SupplyLoadVisualizationProps) {
  // A granel o en LCL no hay pallets que mostrar: la ocupación es por cajas, volumen y peso.
  const noPalletView = estimateOnly && (result.load_mode === 'bulk' || result.shipment_mode === 'lcl');
  const [transportMode, setTransportMode] = useState<'road' | 'sea'>('road');
  const [estimateViewMode, setEstimateViewMode] = useState<'capacity' | 'products' | 'pallets'>(compact ? 'capacity' : 'pallets');
  const [productDataFilter, setProductDataFilter] = useState<'all' | 'included' | 'missing' | 'adjusted'>('all');
  // Tras aplicar un ajuste la estimación se recalcula y el componente se monta de nuevo: la tabla arranca abierta.
  const [productsOpen, setProductsOpen] = useState(() => !estimateOnly || compact || Object.keys(targetColumn?.byProduct ?? {}).length > 0);
  const [roundToFullBoxes, setRoundToFullBoxes] = useState(false);
  const [roundedBoxesApplied, setRoundedBoxesApplied] = useState(false);
  useEffect(() => {
    if (noPalletView && estimateViewMode === 'pallets') {
      setEstimateViewMode('products');
    }
  }, [noPalletView, estimateViewMode]);
  const [layoutView, setLayoutView] = useState<'plan' | 'iso'>(compact ? 'plan' : 'iso');
  const { isRouteEnabled } = useFeatureVisibility();
  const canOpenProductMaster = isRouteEnabled('/settings/products', false);
  const canOpenLogisticsMasters = isRouteEnabled('/settings/logistics', false);
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [productsPage, setProductsPage] = useState(1);
  const [productsPageSize, setProductsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [localExtraBoxesByProduct, setLocalExtraBoxesByProduct] = useState<Record<string, number>>({});
  const extraBoxesByProduct = controlledExtraBoxes ?? localExtraBoxesByProduct;
  const updateExtraBoxes = (updater: (current: Record<string, number>) => Record<string, number>) => {
    const next = updater(extraBoxesByProduct);
    if (onExtraBoxesChange) onExtraBoxesChange(next);
    else setLocalExtraBoxesByProduct(next);
  };
  const [visualPalletOffsets, setVisualPalletOffsets] = useState<Record<number, { x: number; y: number }>>({});
  const draggingVisualPallet = useRef<{
    index: number;
    pointerId: number;
    startX: number;
    startY: number;
    scaleX: number;
    scaleY: number;
    originX: number;
    originY: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } | null>(null);
  useEffect(() => {
    if (!expanded) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [expanded]);
  const container = result.container || {};
  const snapshot = (estimateOnly ? result.before : result.after) || {};
  const utilization = result.utilization || {};
  const usableVolume = numeric(container.usable_volume_m3);
  const { unit: weightUnit, factor: weightFactor } = useWeightUnit();
  const scaleWeight = (value: number | null) => (value == null ? null : value * weightFactor);
  const usableWeight = scaleWeight(numeric(container.usable_payload_kg));
  const volume = numeric(snapshot.volume_m3 ?? (estimateOnly ? utilization.volume_m3_before : utilization.volume_m3_after));
  const weight = scaleWeight(numeric(snapshot.weight_kg ?? (estimateOnly ? utilization.weight_kg_before : utilization.weight_kg_after)));
  const minimumFill = fillLimit(container.minimum_fill_pct);
  const maximumFill = fillLimit(container.maximum_fill_pct);
  const lines = (snapshot.lines_detail || []) as Record<string, unknown>[];

  useEffect(() => {
    setProductsPage(1);
    setProductDataFilter('all');
    setRoundToFullBoxes(false);
    setRoundedBoxesApplied(false);
    if (onExtraBoxesChange) onExtraBoxesChange({});
    else setLocalExtraBoxesByProduct({});
  }, [result, onExtraBoxesChange]);

  const targetByProduct = targetColumn?.byProduct ?? {};
  const targetAdjustedCount = Object.keys(targetByProduct).length;
  const hasTargetColumn = estimateOnly && targetAdjustedCount > 0;
  const targetProposalPending = hasTargetColumn && targetColumn?.state === 'proposal';
  // Una propuesta nueva abre la tabla filtrada a las líneas que cambian (marcar líneas no la reinicia).
  const targetProposalKey = targetProposalPending ? Object.keys(targetByProduct).join(',') : '';
  useEffect(() => {
    if (!targetProposalKey) return;
    setProductsOpen(true);
    setProductDataFilter('adjusted');
    setProductsPage(1);
  }, [targetProposalKey]);

  const rawItems = lines
    .map((line, index) => {
      const orderedUnits = numeric(line.suggested_units ?? line.suggested_quantity) || 0;
      const unitsPerBox = numeric(line.units_per_box);
      const unitsPerPallet = numeric(line.units_per_pallet);
      // Redondear a cajas completas es una regla de compra: manda el mp (box_multiple) cuando agrupa más de una
      // unidad y, si no (mp vacío o 1), las unidades por caja del maestro logístico.
      const purchaseBoxMultiple = numeric(line.box_multiple);
      const roundingStep = purchaseBoxMultiple != null && purchaseBoxMultiple > 1
        ? purchaseBoxMultiple
        : unitsPerBox != null && unitsPerBox > 0 ? unitsPerBox : null;
      const roundedOrderUnits = estimateOnly && roundToFullBoxes && roundingStep != null
        ? Math.ceil((orderedUnits - 1e-9) / roundingStep) * roundingStep
        : orderedUnits;
      const caseRoundingExtraUnits = Math.max(0, roundedOrderUnits - orderedUnits);
      const id = String(line.load_line_id || line.product_id || line.product_name || index + 1);
      const extraBoxes = estimateOnly ? extraBoxesByProduct[id] || 0 : 0;
      const extraUnits = unitsPerBox != null && unitsPerBox > 0 ? extraBoxes * unitsPerBox : 0;
      const units = roundedOrderUnits + extraUnits;
      const unitVolume = numeric(line.effective_unit_volume_m3);
      const unitWeight = scaleWeight(numeric(line.effective_unit_weight_kg));
      return {
        id,
        productId: numeric(line.product_id),
        label: String(line.product_name || line.sku || line.product_id || index + 1),
        supplierId: numeric(line.supplier_id),
        supplierName: String(line.supplier_name || ''),
        sku: String(line.sku || ''),
        logisticsDataSource: String(line.logistics_data_source || ''),
        orderedUnits,
        roundedOrderUnits,
        caseRoundingExtraUnits,
        units,
        unitsPerBox,
        roundingStep,
        unitsPerPallet,
        boxesPerLayer: numeric(line.boxes_per_base),
        layersPerPallet: numeric(line.bases_per_pallet),
        palletLengthMm: numeric(line.pallet_length_mm),
        palletWidthMm: numeric(line.pallet_width_mm),
        palletHeightMm: numeric(line.pallet_loaded_height_mm),
        extraBoxes,
        extraUnits,
        unitVolume,
        unitWeight,
        estimatedVolume: (unitVolume || 0) * units,
        baseEstimatedVolume: (unitVolume || 0) * roundedOrderUnits,
        estimatedWeight: unitWeight == null ? null : unitWeight * units,
        baseEstimatedWeight: unitWeight == null ? null : unitWeight * roundedOrderUnits,
        extraVolume: (unitVolume || 0) * extraUnits,
        extraWeight: unitWeight == null ? null : unitWeight * extraUnits,
        boxCount: unitsPerBox != null && unitsPerBox > 0 ? units / unitsPerBox : null,
        palletCount: unitsPerPallet != null && unitsPerPallet > 0 ? Math.ceil(units / unitsPerPallet) : null,
        palletEquivalent: unitsPerPallet != null && unitsPerPallet > 0 ? units / unitsPerPallet : null,
        stackable: typeof line.stackable === 'boolean' ? line.stackable : null,
        fragile: line.fragile === true,
        maxStackPallets: numeric(line.max_stack_pallets),
        color: productColor(index),
        isNewAssortment: false,
      };
    })
    .filter((line) => line.units > 0);
  const addedBoxCount = rawItems.reduce((total, item) => total + item.extraBoxes, 0);
  const addedUnitCount = rawItems.reduce((total, item) => total + item.extraUnits, 0);
  const caseRoundingProductCount = rawItems.filter((item) => item.caseRoundingExtraUnits > 0).length;
  const caseRoundingUnitCount = rawItems.reduce((total, item) => total + item.caseRoundingExtraUnits, 0);
  const addedVolume = rawItems.reduce((total, item) => total + item.extraVolume, 0);
  const addedWeight = rawItems.reduce((total, item) => total + (item.extraWeight ?? 0), 0);
  const lineEstimatedVolume = rawItems.reduce((total, item) => total + item.baseEstimatedVolume, 0);
  const allLineWeightsKnown = rawItems.every((item) => item.baseEstimatedWeight != null);
  const lineEstimatedWeight = allLineWeightsKnown
    ? rawItems.reduce((total, item) => total + (item.baseEstimatedWeight || 0), 0)
    : null;
  const lineEstimatedKnownWeight = rawItems.reduce((total, item) => total + (item.baseEstimatedWeight || 0), 0);
  const displayedVolume = estimateOnly
    ? (roundToFullBoxes ? lineEstimatedVolume : volume ?? lineEstimatedVolume) + addedVolume
    : volume;
  const displayedWeightBase = estimateOnly && roundToFullBoxes
    ? lineEstimatedKnownWeight
    : weight ?? lineEstimatedWeight;
  const displayedWeight = estimateOnly
    ? displayedWeightBase == null ? null : displayedWeightBase + addedWeight
    : weight;
  const volumeFill = estimateOnly
    ? displayedVolume != null && usableVolume ? (displayedVolume / usableVolume) * 100 : null
    : numeric(utilization.volume_fill_pct_after) ??
      (displayedVolume != null && usableVolume ? (displayedVolume / usableVolume) * 100 : null);
  const weightFill = estimateOnly
    ? displayedWeight != null && usableWeight ? (displayedWeight / usableWeight) * 100 : null
    : numeric(utilization.weight_fill_pct_after) ??
      (displayedWeight != null && usableWeight ? (displayedWeight / usableWeight) * 100 : null);
  const palletizedItems = rawItems
    .filter((item) => item.palletCount != null)
    .sort((a, b) => b.estimatedVolume - a.estimatedVolume);
  const stowagePlan = estimateOnly ? null : result.stowage_plan || null;
  const placements = stowagePlan?.placements || [];
  // A side elevation collapses pallets on opposite sides of the container onto
  // the same pixels. For a readable load plan, show their real floor footprints.
  const topPlacementsByPosition = new Map<string, (typeof placements)[number]>();
  const stackCountByPosition = new Map<string, number>();
  placements.forEach((placement) => {
    const key = `${placement.x_mm}:${placement.y_mm}`;
    stackCountByPosition.set(key, (stackCountByPosition.get(key) || 0) + 1);
    const current = topPlacementsByPosition.get(key);
    if (!current || placement.z_mm > current.z_mm) topPlacementsByPosition.set(key, placement);
  });
  const displayPlacements = [...topPlacementsByPosition.values()].sort((a, b) =>
    a.y_mm - b.y_mm || a.x_mm - b.x_mm,
  );
  const placementCountByProduct = new Map<number, number>();
  placements.forEach((placement) => {
    placementCountByProduct.set(
      placement.product_id,
      (placementCountByProduct.get(placement.product_id) || 0) + 1,
    );
  });
  const colorByProduct = new Map(rawItems.map((item) => [item.productId, item.color]));
  const stowageDimensions = stowagePlan?.container_dimensions_mm || {
    length_mm: numeric(container.internal_length_mm),
    width_mm: numeric(container.internal_width_mm),
    height_mm: numeric(container.internal_height_mm),
  };
  const stowageComplete = stowagePlan?.complete === true;

  // Compartimento interior del camión y container blanco resaltado en el barco.
  // En envío LCL se contrata volumen o peso, no un vehículo: el espacio contratado se dibuja solo, sin camión ni barco.
  const isLcl = estimateOnly && result.shipment_mode === 'lcl';
  const cargoX = isLcl ? 20 : transportMode === 'sea' ? 24 : 130;
  const cargoY = isLcl ? 28 : transportMode === 'sea' ? 78 : 35;
  const cargoWidth = isLcl ? 660 : transportMode === 'sea' ? 652 : 555;
  const cargoHeight = isLcl ? 105 : transportMode === 'sea' ? 64 : 105;
  const lclCapacityLabel = [
    usableVolume != null ? `${usableVolume.toLocaleString(undefined, { maximumFractionDigits: 2 })} m³` : null,
    usableWeight != null ? `${usableWeight.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${weightUnit}` : null,
  ].filter((item): item is string => item != null).join(' · ');
  const containerLength = numeric(stowageDimensions?.length_mm);
  const containerWidth = numeric(stowageDimensions?.width_mm);
  const containerHeight = numeric(stowageDimensions?.height_mm);
  const estimatedItems = rawItems
    .filter((item) => item.estimatedVolume > 0)
    .sort((a, b) => b.estimatedVolume - a.estimatedVolume);
  // Vista isométrica: el plan de estiba real, o un reparto ilustrativo con los pallets estimados.
  const isoDims = {
    length: containerLength || DEFAULT_ISO_DIMS.length,
    width: containerWidth || DEFAULT_ISO_DIMS.width,
    height: containerHeight || DEFAULT_ISO_DIMS.height,
  };
  const isoIllustrative = placements.length === 0;
  const isoAvailable = placements.length > 0
    || (estimateOnly && estimateViewMode === 'pallets' && rawItems.some((item) => (item.palletCount ?? 0) > 0));
  const showIso = isoAvailable && layoutView === 'iso';
  const isoPalletItems = showIso && placements.length === 0
    ? estimatedItems.filter((item) => (item.palletEquivalent ?? 0) > 0)
    : [];
  // Pallets ilustrativos de la vista plana: el mismo reparto por pallets equivalentes que el 3D.
  const estimatePalletPlan = estimateOnly
    ? planPallets(estimatedItems.filter((item) => (item.palletEquivalent ?? 0) > 0).map((item) => ({
      productId: item.productId,
      label: item.label,
      color: item.color,
      palletEquivalent: item.palletEquivalent ?? 0,
      layers: item.layersPerPallet,
      boxesPerLayer: item.boxesPerLayer,
      stackable: item.stackable,
      fragile: item.fragile,
      maxStackPallets: item.maxStackPallets,
    })))
    : { whole: [], mixed: [] };
  const itemByProductId = new Map(estimatedItems.map((item) => [item.productId, item]));
  // Un pallet físico por casilla: completo (un producto) o mixto (varios tramos con su parte del pallet).
  const estimatePalletSlots: Array<{ shares: Array<{ item: (typeof estimatedItems)[number]; fraction: number }> }> = [
    ...estimatePalletPlan.whole.flatMap(({ demand, count }) => {
      const item = itemByProductId.get(demand.productId);
      return item ? Array.from({ length: Math.min(count, 40) }, () => ({ shares: [{ item, fraction: 1 }] })) : [];
    }),
    ...estimatePalletPlan.mixed.map((shares) => ({
      shares: shares.flatMap(({ demand, fraction }) => {
        const item = itemByProductId.get(demand.productId);
        return item ? [{ item, fraction }] : [];
      }),
    })).filter((slot) => slot.shares.length > 0),
  ];
  const toFrontSlot = (slot: (typeof estimatePalletSlots)[number]): PalletFrontSlot => ({
    shares: slot.shares.map((share) => ({
      label: share.item.label,
      index: displayIndexByProduct.get(share.item.productId) ?? null,
      fraction: share.fraction,
      layers: share.item.layersPerPallet,
      boxesPerLayer: share.item.boxesPerLayer,
      stackable: share.item.stackable,
      unitsPerPallet: share.item.unitsPerPallet,
      palletLengthMm: share.item.palletLengthMm,
      palletWidthMm: share.item.palletWidthMm,
      palletHeightMm: share.item.palletHeightMm,
    })),
  });
  const isoLayout = !showIso
    ? { blocks: [], overflow: 0, partialProducts: 0 }
    : placements.length > 0
      ? { blocks: blocksFromPlacements(placements, (productId) => colorByProduct.get(productId) || '#64748b'), overflow: Math.max(0, (stowagePlan?.total_pallets ?? 0) - (stowagePlan?.placed_pallets ?? 0)), partialProducts: 0 }
      : synthesizeBlocks(
        isoPalletItems.map((item) => ({
          productId: item.productId,
          label: item.label,
          color: item.color,
          palletEquivalent: item.palletEquivalent ?? 0,
          layers: item.layersPerPallet,
          boxesPerLayer: item.boxesPerLayer,
          stackable: item.stackable,
          fragile: item.fragile,
          maxStackPallets: item.maxStackPallets,
        })),
        isoDims,
      );
  const palletEnvelopeVolume = placements.reduce(
    (total, placement) => total + (
      placement.oriented_length_mm * placement.oriented_width_mm * placement.height_mm
    ) / 1_000_000_000,
    0,
  );
  const containerGeometricVolume = containerLength && containerWidth && containerHeight
    ? (containerLength * containerWidth * containerHeight) / 1_000_000_000
    : null;
  const palletEnvelopeFill = containerGeometricVolume
    ? (palletEnvelopeVolume / containerGeometricVolume) * 100
    : null;
  const previewItems = estimateOnly ? estimatedItems : estimatedItems.slice(0, 8);
  // Keep unplaced SKUs visible in the legend so a partial plan is understandable.
  const visualProducts = placements.length > 0 ? palletizedItems : estimateOnly ? estimatedItems : previewItems;
  // Productos que la propuesta añade al ampliar surtido: no están en la estimación (cantidad 0),
  // así que se muestran como filas propias para poder aceptarlos o rechazarlos.
  const rawProductIds = new Set(rawItems.map((item) => item.productId));
  const newAssortmentItems: typeof rawItems = hasTargetColumn
    ? Object.entries(targetByProduct)
      .filter(([productId]) => !rawProductIds.has(Number(productId)))
      .map(([productId, adjustment], index) => ({
        id: `new-${productId}`,
        productId: Number(productId),
        label: adjustment.label || adjustment.sku || productId,
        supplierId: null,
        supplierName: '',
        sku: adjustment.sku || '',
        logisticsDataSource: '',
        orderedUnits: 0,
        roundedOrderUnits: 0,
        caseRoundingExtraUnits: 0,
        units: 0,
        unitsPerBox: null,
        roundingStep: null,
        unitsPerPallet: null,
        extraBoxes: 0,
        extraUnits: 0,
        unitVolume: null,
        unitWeight: null,
        estimatedVolume: 0,
        baseEstimatedVolume: 0,
        estimatedWeight: null,
        baseEstimatedWeight: null,
        extraVolume: 0,
        extraWeight: null,
        boxCount: null,
        palletCount: null,
        palletEquivalent: null,
        boxesPerLayer: null,
        layersPerPallet: null,
        palletLengthMm: null,
        palletWidthMm: null,
        palletHeightMm: null,
        stackable: null,
        fragile: false,
        maxStackPallets: null,
        color: productColor(rawItems.length + index),
        isNewAssortment: true,
      }))
    : [];
  const detailProducts = estimateOnly ? [...rawItems, ...newAssortmentItems] : visualProducts;
  const missingLogisticsLineIds = new Set((result.missing_data || [])
    .filter((item) => Number(item.product_id) > 0)
    .map((item) => `${String(('supplier_id' in item ? item.supplier_id : '') ?? '')}:${String(item.product_id)}`));
  const isMissingLogisticsItem = (item: (typeof rawItems)[number]) =>
    missingLogisticsLineIds.has(`${String(item.supplierId ?? '')}:${String(item.productId ?? '')}`);
  const missingItems = rawItems.filter(isMissingLogisticsItem);
  const missingProductsCount = missingItems.length;
  const missingSupplierIds = [...new Set(missingItems.map((item) => item.supplierId).filter((id): id is number => id != null))];
  const includedProductsCount = Math.max(0, rawItems.length - missingProductsCount);
  const effectiveDataFilter = productDataFilter === 'adjusted' && !hasTargetColumn ? 'all' : productDataFilter;
  const isTargetAdjusted = (item: (typeof rawItems)[number]) => item.productId != null && item.productId in targetByProduct;
  const filteredProducts = detailProducts.filter((item) => {
    const isMissing = estimateOnly && isMissingLogisticsItem(item);
    const matchesDataFilter = !estimateOnly || effectiveDataFilter === 'all'
      || (effectiveDataFilter === 'missing' && isMissing)
      || (effectiveDataFilter === 'included' && !isMissing)
      || (effectiveDataFilter === 'adjusted' && isTargetAdjusted(item));
    const matchesSearch = `${item.label} ${item.sku}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
    return matchesDataFilter && matchesSearch;
  });
  const productsTotalPages = Math.max(1, Math.ceil(filteredProducts.length / productsPageSize));
  const safeProductsPage = Math.min(productsPage, productsTotalPages);
  const pagedProducts = estimateOnly
    ? filteredProducts.slice((safeProductsPage - 1) * productsPageSize, safeProductsPage * productsPageSize)
    : filteredProducts;
  const filteredUnplacedProducts = (stowagePlan?.unplaced_products || []).filter((item) =>
    `${String(item.product_name || '')} ${String(item.sku || '')} ${String(item.reason || '')}`
      .toLocaleLowerCase()
      .includes(search.trim().toLocaleLowerCase()),
  );
  const unplacedProductIds = new Set((stowagePlan?.unplaced_products || []).map((item) => String(item.product_id)));
  const remainingMissingData = (result.missing_data || []).filter((item) => !unplacedProductIds.has(String(item.product_id)));
  const filteredMissingData = remainingMissingData.filter((item) => {
    const product = rawItems.find((line) => line.productId === item.product_id);
    return !search.trim() || `${product?.label || ''} ${item.fields.join(' ')}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
  });
  const displayIndexByProduct = new Map(visualProducts.map((item, index) => [item.productId, index + 1]));
  const totalOrderProductCount = new Set(rawItems.map((item) => item.productId ?? item.id)).size;
  const placedProductCount = new Set(placements.map((placement) => placement.product_id)).size;
  const estimatedVolume = estimatedItems.reduce((sum, item) => sum + item.estimatedVolume, 0);
  const estimatedFill = usableVolume && estimatedVolume > 0 ? (estimatedVolume / usableVolume) * 100 : null;
  const shownFillPct = compact && fillPct != null ? fillPct : (volumeFill ?? estimatedFill ?? null);
  const occupiedRatio = Math.max(0, Math.min(1, (shownFillPct ?? 100) / 100));
  // Reparto del relleno por clasificación ABC o por proveedor, en proporción a su volumen: cada grupo ocupa la parte
  // del contenedor que le toca de lo ya ocupado (los grupos suman el total ocupado, shownFillPct).
  const fillGroups = (() => {
    if (!(compact && colorMode)) return null;
    const groups = new Map<string, { color: string; volume: number }>();
    rawItems.forEach((item) => {
      const key = colorMode === 'supplier' ? String(item.supplierId ?? '') : String(productContext?.[item.productId ?? -1]?.abcCategory ?? '');
      const color = colorMode === 'supplier'
        ? (item.supplierId != null ? supplierColor(item.supplierId) : NO_ABC_COLOR)
        : ABC_COLORS[key] ?? NO_ABC_COLOR;
      const entry = groups.get(key) ?? { color, volume: 0 };
      entry.volume += item.estimatedVolume;
      groups.set(key, entry);
    });
    // Sin ninguna clase ABC conocida (p. ej. mientras carga) no se pinta de gris: relleno neutro, sin grupos.
    if (colorMode === 'abc' && [...groups.keys()].every((key) => key === '')) groups.clear();
    const totalVolume = [...groups.values()].reduce((total, group) => total + group.volume, 0);
    if (groups.size === 0 || totalVolume <= 0) return null;
    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, group]) => ({ key, color: group.color, pct: (group.volume / totalVolume) * (shownFillPct ?? 0) }));
  })();
  // rawItems (y por lo tanto fillGroups) se recalcula en cada render: solo se avisa hacia afuera cuando el
  // contenido realmente cambia, para no entrar en un ciclo de renders con quien escucha este aviso.
  const reportedFillPctRef = useRef('');
  useEffect(() => {
    if (!onGroupFillPct) return;
    const next = Object.fromEntries((fillGroups ?? []).map((group) => [group.key, group.pct]));
    const serialized = JSON.stringify(next);
    if (serialized === reportedFillPctRef.current) return;
    reportedFillPctRef.current = serialized;
    onGroupFillPct(next);
  }, [fillGroups, onGroupFillPct]);
  // Columnas de la tabla tras el producto, agrupadas en pedido (unidades y ajuste al objetivo),
  // inventario (si hay contexto de la tabla de supply) y carga estimada.
  // HU1: % de pallets (sobre las posiciones de suelo del vehículo, con pallets de 1,2 × 0,8 m) y qué restricción limita.
  const palletPositions = containerLength && containerWidth
    ? Math.floor(containerLength / 1200) * Math.floor(containerWidth / 800)
    : null;
  const palletsNeeded = estimateOnly && !noPalletView
    ? Math.ceil(rawItems.reduce((total, item) => total + (item.palletEquivalent ?? 0), 0) - 1e-9)
    : null;
  const palletFill = palletsNeeded != null && palletPositions ? (palletsNeeded / palletPositions) * 100 : null;
  const limiting = estimateOnly
    ? (([['volume', volumeFill], ['weight', weightFill], ['pallets', palletFill]] as Array<[string, number | null]>)
      .filter((entry): entry is [string, number] => entry[1] != null && entry[1] > 0)
      .sort((a, b) => b[1] - a[1])[0] ?? null)
    : null;
  const limitingPill = (key: string) => (limiting?.[0] === key
    ? <span className={estimateOnly ? 'mb-1.5 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900' : 'absolute right-2 top-2 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-white'}>{t('supply.supplyAgent.loadLimitingBadge')}</span>
    : null);
  const limitingRing = (key: string) => (limiting?.[0] === key ? ' ring-2 ring-slate-900/70' : '');
  // En la estimación las métricas van en una sola caja dividida y el color queda para el estado (límite = ámbar).
  const metricCardClass = (tint: string, key: string) => (estimateOnly ? 'relative p-3' : `relative rounded-lg border ${tint} p-3${limitingRing(key)}`);
  const metricBarClass = (key: string, fallback: string) => (estimateOnly ? (limiting?.[0] === key ? 'bg-amber-500' : 'bg-sky-500') : fallback);
  const showProductContext = estimateOnly && productContext != null && Object.keys(productContext).length > 0;
  type ProductColumn = { key: string; label: string; width: number; group: 'order' | 'inventory' | 'load'; align?: 'left' };
  const productColumns: ProductColumn[] = [
    { key: 'units', label: t('supply.supplyAgent.loadUnitsColumn'), width: 58, group: 'order' },
    ...(hasTargetColumn ? [{ key: 'target', label: t('supply.supplyAgent.loadTargetColumn'), width: 132, group: 'order' as const, align: 'left' as const }] : []),
    ...(showProductContext ? [
      { key: 'stock', label: t('supply.supplyAgent.loadStockColumn'), width: 64, group: 'inventory' as const },
      { key: 'sales', label: t('supply.supplyAgent.loadMonthlySalesColumn'), width: 56, group: 'inventory' as const },
      { key: 'salesEst', label: t('supply.supplyAgent.loadEstimatedSalesColumn'), width: 56, group: 'inventory' as const },
      { key: 'inTransit', label: t('supply.supplyAgent.loadInTransitColumn'), width: 62, group: 'inventory' as const },
      { key: 'coverage', label: t('supply.supplyAgent.loadCoverageColumn'), width: 64, group: 'inventory' as const },
      { key: 'coverageFuture', label: t('supply.supplyAgent.loadCoverageFutureColumn'), width: 72, group: 'inventory' as const },
      { key: 'abc', label: t('supply.supplyAgent.loadAbcColumn'), width: 40, group: 'inventory' as const },
    ] : []),
    { key: 'boxes', label: t('supply.supplyAgent.loadBoxesColumn'), width: 50, group: 'load' },
    ...(noPalletView ? [] : [
      { key: 'boxesPerLayer', label: t('supply.supplyAgent.loadBoxesPerLayerColumn'), width: 56, group: 'load' as const },
      { key: 'layers', label: t('supply.supplyAgent.loadLayersColumn'), width: 46, group: 'load' as const },
    ]),
    ...(noPalletView ? [] : [{ key: 'pallets', label: t('supply.supplyAgent.loadPalletsEqColumn'), width: 54, group: 'load' as const }]),
    ...(estimateOnly ? [{ key: 'extra', label: t('supply.supplyAgent.loadAddBoxesColumn'), width: 58, group: 'load' as const }] : []),
  ];
  const showColumnGroups = showProductContext;
  const productColumnGroups = (['order', 'inventory', 'load'] as const)
    .map((group) => ({
      key: group,
      span: productColumns.filter((column) => column.group === group).length,
      label: t(group === 'order' ? 'supply.supplyAgent.loadGroupOrder' : group === 'inventory' ? 'supply.supplyAgent.loadGroupInventory' : 'supply.supplyAgent.loadGroupLoad'),
      className: group === 'order' ? 'border-sky-300 text-sky-800' : group === 'inventory' ? 'border-indigo-200 text-indigo-700' : 'border-slate-300 text-slate-600',
    }))
    .filter((group) => group.span > 0);
  // La tabla siempre cabe en el ancho disponible: el producto absorbe el espacio sobrante y recorta el nombre.
  const productGridStyle = {
    gridTemplateColumns: ['minmax(130px,1fr)', ...productColumns.map((column) => `${column.width}px`)].join(' '),
  };
  const formatContextQuantity = (value: number | null | undefined) => (value == null || !Number.isFinite(value) ? '—' : formatLoadQuantity(value, 0));
  const maxExtraBoxesAllowed = (item: (typeof rawItems)[number]) => {
    if (!estimateOnly || item.unitsPerBox == null || item.unitsPerBox <= 0 ||
      item.unitVolume == null || item.unitVolume <= 0 || item.unitWeight == null || item.unitWeight <= 0 ||
      item.unitsPerPallet == null || item.unitsPerPallet <= 0 ||
      usableVolume == null || usableWeight == null || displayedVolume == null || displayedWeight == null) return 0;
    const boxVolume = item.unitVolume * item.unitsPerBox;
    const boxWeight = item.unitWeight * item.unitsPerBox;
    const volumeWithoutThisProductExtras = displayedVolume - item.extraVolume;
    const weightWithoutThisProductExtras = displayedWeight - (item.extraWeight || 0);
    const byVolume = Math.floor((usableVolume - volumeWithoutThisProductExtras + 0.000001) / boxVolume);
    const byWeight = Math.floor((usableWeight - weightWithoutThisProductExtras + 0.01) / boxWeight);
    // Las posiciones de suelo libres también limitan: las cajas extra ocupan pallets del mismo producto.
    const palletsInUse = rawItems.reduce((total, line) => total + (line.palletEquivalent ?? 0), 0) - item.extraUnits / item.unitsPerPallet;
    const byPallets = estimateOnly && !noPalletView && palletPositions
      ? Math.floor(((palletPositions - palletsInUse) * item.unitsPerPallet + 0.000001) / item.unitsPerBox)
      : Number.POSITIVE_INFINITY;
    return Math.max(0, Math.min(byVolume, byWeight, byPallets));
  };

  return (
    <section className={`${expanded ? 'fixed inset-3 z-[100] flex flex-col overflow-y-auto shadow-2xl sm:inset-6' : estimateOnly ? 'overflow-hidden' : 'overflow-hidden shadow-sm'} ${estimateOnly && !expanded ? '' : 'rounded-xl border border-slate-200'} bg-white`} aria-label={t(transportMode === 'sea' ? 'supply.supplyAgent.loadVisualTitleSea' : 'supply.supplyAgent.loadVisualTitle')}>
      <style>{MOTION_STYLES}</style>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          {estimateOnly ? null : <p className="text-sm font-semibold text-slate-900">{t(transportMode === 'sea' ? 'supply.supplyAgent.loadVisualTitleSea' : 'supply.supplyAgent.loadVisualTitle')}</p>}
          <p className={`${estimateOnly ? '' : 'mt-1 '}text-xs text-slate-500`}>
            <span className="mr-1.5 rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">{t(estimateOnly ? (estimateViewMode === 'capacity' ? 'supply.supplyAgent.loadEstimateBasis' : 'supply.supplyAgent.loadEstimateBasisProducts') : 'supply.supplyAgent.loadPlacementPlanLabel')}</span>
            {String(container.container_code || t('supply.supplyAgent.loadGenericTruck'))}
            {container.description ? ` · ${String(container.description)}` : ''}
          </p>
          {stowagePlan ? <p className="mt-1.5 inline-flex rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-800">{t('supply.supplyAgent.loadRepresentedProducts', { placed: placedProductCount, total: totalOrderProductCount })}</p> : null}
          {estimateOnly && !compact ? <p className="mt-1.5 text-[11px] text-slate-500">{t(estimateViewMode === 'pallets' ? 'supply.supplyAgent.loadEstimatePalletsExplanation' : estimateViewMode === 'products' ? 'supply.supplyAgent.loadEstimateProductsExplanation' : 'supply.supplyAgent.loadEstimateExplanation')}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setExpanded((value) => !value)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50" aria-label={t(expanded ? 'supply.supplyAgent.loadCollapse' : 'supply.supplyAgent.loadExpand')}>
            {expanded ? <Minimize2 size={14} aria-hidden="true" /> : <Maximize2 size={14} aria-hidden="true" />}
            {t(expanded ? 'supply.supplyAgent.loadCollapse' : 'supply.supplyAgent.loadExpand')}
          </button>
          {isLcl || compact ? null : <div role="group" aria-label={t('supply.supplyAgent.loadTransportLabel')} className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5">
            <button type="button" aria-pressed={transportMode === 'road'} onClick={() => setTransportMode('road')} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${transportMode === 'road' ? 'bg-white text-sky-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
              <Truck size={14} aria-hidden="true" />{t('supply.supplyAgent.loadTransportRoad')}
            </button>
            <button type="button" aria-pressed={transportMode === 'sea'} onClick={() => setTransportMode('sea')} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${transportMode === 'sea' ? 'bg-white text-sky-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
              <Ship size={14} aria-hidden="true" />{t('supply.supplyAgent.loadTransportSea')}
            </button>
          </div>}
          {isoAvailable && !compact ? (
            <div role="group" aria-label={t('supply.supplyAgent.loadViewLabel')} className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5">
              {(['iso', 'plan'] as const).map((view) => (
                <button key={view} type="button" aria-pressed={layoutView === view} onClick={() => setLayoutView(view)} className={`rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${layoutView === view ? 'bg-white text-sky-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
                  {t(view === 'iso' ? 'supply.supplyAgent.loadView3d' : 'supply.supplyAgent.loadView2d')}
                </button>
              ))}
            </div>
          ) : null}
          {!estimateOnly ? <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-800">
            {stowageComplete
              ? t('supply.supplyAgent.loadStowageReady')
              : t(stowagePlan?.status === 'BLOCKED'
                ? 'supply.supplyAgent.loadStowageBlocked'
                : 'supply.supplyAgent.loadStowageIncomplete')}
          </span> : null}
          {estimateOnly && !compact ? (
            <>
              <div role="group" aria-label={t('supply.supplyAgent.loadEstimateViewLabel')} className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5">
                <button type="button" aria-pressed={estimateViewMode === 'capacity'} onClick={() => { setEstimateViewMode('capacity'); }} className={`rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${estimateViewMode === 'capacity' ? 'bg-white text-sky-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
                  {t(result.shipment_mode === 'lcl' ? 'supply.supplyAgent.loadEstimateViewCapacityLcl' : 'supply.supplyAgent.loadEstimateViewCapacity')}
                </button>
                <button type="button" aria-pressed={estimateViewMode === 'products'} onClick={() => { setEstimateViewMode('products'); }} className={`rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${estimateViewMode === 'products' ? 'bg-white text-sky-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
                  {t('supply.supplyAgent.loadEstimateViewProducts')}
                </button>
{noPalletView ? null :                 <button type="button" aria-pressed={estimateViewMode === 'pallets'} onClick={() => setEstimateViewMode('pallets')} className={`rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${estimateViewMode === 'pallets' ? 'bg-white text-sky-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
                  {t('supply.supplyAgent.loadEstimateViewPallets')}
                </button>}
              </div>
            </>
          ) : null}
        </div>
      </header>

      <div className={`bg-gradient-to-b from-slate-50 via-white to-slate-50 px-3 pt-3 ${expanded ? 'flex-1' : ''}`}>
        {showIso ? (
          <SupplyIsometricLoadView
            dims={isoDims}
            blocks={isoLayout.blocks}
            overflow={isoLayout.overflow}
            transportMode={transportMode}
            selectedProductId={selectedProductId}
            onSelectProduct={setSelectedProductId}
            indexByProduct={displayIndexByProduct}
            illustrative={isoIllustrative}
            smallProducts={isoIllustrative ? isoLayout.partialProducts : 0}
            fillPct={volumeFill}
            weight={displayedWeight}
            weightUnit={weightUnit}
            expanded={expanded}
            t={t}
          />
        ) : <svg viewBox={`0 0 700 ${isLcl ? 165 : 235}`} role="img" aria-label={t('supply.supplyAgent.loadPlacementPlanView')} className={`mx-auto h-auto w-full ${expanded ? 'max-w-none' : 'max-w-5xl'}`}>
          <defs>
            <clipPath id="supplyCargoClip">
              <rect x={cargoX} y={cargoY} width={cargoWidth} height={cargoHeight} rx="5" />
            </clipPath>
          </defs>
          {isLcl ? null : <image
            href={transportMode === 'sea' ? '/assets/logistics/container-ship-side.png' : '/assets/logistics/truck-side.png'}
            x="0"
            y="0"
            width="700"
            height="235"
            preserveAspectRatio="none"
            aria-hidden="true"
          />}
          {isLcl ? (
            <text x={cargoX} y={cargoY - 9} fill="#334155" fontSize="9" fontWeight="700">
              {t('supply.supplyAgent.loadLclSpaceLabel', { capacity: lclCapacityLabel })}
            </text>
          ) : null}
          <rect
            x={cargoX - 3}
            y={cargoY - 3}
            width={cargoWidth + 6}
            height={cargoHeight + 6}
            rx="4"
            fill="#ffffff"
            fillOpacity="0.08"
            stroke={transportMode === 'sea' ? '#cbd5e1' : '#64748b'}
            strokeWidth={transportMode === 'sea' ? 1 : 2}
          />
          <g clipPath="url(#supplyCargoClip)">
            <rect x={cargoX} y={cargoY} width={cargoWidth} height={cargoHeight} fill="#ffffff" opacity={transportMode === 'sea' ? 0.06 : 0.18} />
            <path d={`M ${cargoX + 3} ${cargoY + cargoHeight - 3} H ${cargoX + cargoWidth - 3}`} stroke="#334155" strokeWidth="1.5" />
            {containerLength && containerWidth && containerHeight && displayPlacements.map((placement, placementIndex) => {
              const innerWidth = cargoWidth - 8;
              const innerHeight = cargoHeight - 8;
              const x = cargoX + 3 + (placement.x_mm / containerLength) * innerWidth;
              const y = cargoY + cargoHeight - 3
                - ((placement.y_mm + placement.oriented_width_mm) / containerWidth) * innerHeight;
              const width = (placement.oriented_length_mm / containerLength) * innerWidth;
              const height = (placement.oriented_width_mm / containerWidth) * innerHeight;
              const color = colorByProduct.get(placement.product_id) || '#64748b';
              const showLabel = width > 34 && height > 14;
              const shortLabel = placement.product_name || placement.sku;
              const labelLines = wrapPalletLabel(shortLabel, Math.max(8, Math.floor(width / 4.6)));
              const labelStartY = y + height / 2 + 2 - ((labelLines.length - 1) * 4);
              return (
                <g
                  key={`${placement.stack_id}-${placement.level}`}
                  className="sv-pop sv-tile"
                  style={{ animationDelay: `${Math.min(placementIndex * 22, 900)}ms` }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectedProductId === placement.product_id}
                  aria-label={placement.product_name || placement.sku}
                  onClick={() => setSelectedProductId(selectedProductId === placement.product_id ? null : placement.product_id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedProductId(selectedProductId === placement.product_id ? null : placement.product_id);
                    }
                  }}
                >
                  <title>{`${placement.product_name} · pallet ${placement.pallet_index} · huella ${placement.oriented_length_mm} × ${placement.oriented_width_mm} mm · altura ${placement.height_mm} mm · x ${placement.x_mm} mm, y ${placement.y_mm} mm${placement.rotated ? ' · rotado' : ''}`}</title>
                  <rect x={x} y={y} width={Math.max(2, width)} height={Math.max(2, height)} rx="1.5" fill={color} fillOpacity={selectedProductId == null || selectedProductId === placement.product_id ? (placement.fragile ? 0.88 : 0.96) : 0.28} stroke={selectedProductId === placement.product_id ? '#0f172a' : '#fff'} strokeWidth={selectedProductId === placement.product_id ? 2 : 1.1} />
                  {width > 17 && height > 14 ? <text x={x + 3} y={y + 8} fill="white" fontSize="6.5" fontWeight="800">{String(displayIndexByProduct.get(placement.product_id) || '')}</text> : null}
                  {showLabel ? (
                    <text x={x + width / 2} textAnchor="middle" fill="white" fontSize="7.5" fontWeight="700">
                      {labelLines.map((line, index) => <tspan key={index} x={x + width / 2} y={labelStartY + index * 8}>{line}</tspan>)}
                    </text>
                  ) : null}
                  {(stackCountByPosition.get(`${placement.x_mm}:${placement.y_mm}`) || 0) > 1 ? (
                    <g>
                      <circle cx={x + width - 7} cy={y + 7} r="6" fill="#0f172a" fillOpacity="0.82" />
                      <text x={x + width - 7} y={y + 9.5} textAnchor="middle" fill="white" fontSize="6" fontWeight="700">
                        {stackCountByPosition.get(`${placement.x_mm}:${placement.y_mm}`)}
                      </text>
                    </g>
                  ) : null}
                </g>
              );
            })}
            {placements.length === 0 && previewItems.length > 0 && estimateOnly && estimateViewMode === 'capacity' ? (() => {
              const innerX = cargoX + 4;
              const innerY = cargoY + 5;
              const innerWidth = cargoWidth - 8;
              const innerHeight = cargoHeight - 10;
              // En el módulo de estimación un llenado pequeño sigue viéndose y se lee con su porcentaje.
              const occupiedWidth = compact && (shownFillPct ?? 0) > 0 ? Math.max(innerWidth * occupiedRatio, 4) : innerWidth * occupiedRatio;
              const emptyX = innerX + occupiedWidth;
              const fillLabel = `${(shownFillPct ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
              return (
                <g>
                  {occupiedWidth > 0 ? (() => {
                    if (!fillGroups) {
                      return <rect key={`occupied-${Math.round(occupiedWidth)}`} className="sv-growx" x={innerX} y={innerY} width={occupiedWidth} height={innerHeight} fill="#0ea5e9" fillOpacity="0.8" />;
                    }
                    let cursor = innerX;
                    return <g key={`occupied-${Math.round(occupiedWidth)}-${fillGroups.map((group) => group.key).join('|')}`}>
                      {fillGroups.map((group) => {
                        // El ancho sale del mismo porcentaje que se muestra como texto (del contenedor completo),
                        // así que todos los grupos suman exactamente lo ocupado.
                        const width = (group.pct / 100) * innerWidth;
                        const x = cursor;
                        cursor += width;
                        return (
                          <g key={group.key}>
                            <rect className="sv-growx" x={x} y={innerY} width={width} height={innerHeight} fill={group.color} fillOpacity="0.85" />
                            {width > 22 ? (
                              <text x={x + width / 2} y={innerY + innerHeight / 2 + 3} textAnchor="middle" fill="white" fontSize="8" fontWeight="700">
                                {`${group.pct.toFixed(0)}%`}
                              </text>
                            ) : null}
                          </g>
                        );
                      })}
                    </g>;
                  })() : null}
                  {occupiedWidth < innerWidth ? <rect x={emptyX} y={innerY} width={innerX + innerWidth - emptyX} height={innerHeight} fill="#ffffff" fillOpacity={transportMode === 'sea' ? 0.16 : 0.32} /> : null}
                  {compact && (shownFillPct ?? 0) > 0 ? (() => {
                    // Mismo lugar y mismo texto sea cual sea el porcentaje: a la derecha de la barra y, si ya casi no queda
                    // camión libre, dentro de ella y alineado a su borde.
                    const label = `${fillLabel} · ${estimatedItems.length} ${t('supply.supplyAgent.loadEstimateShortProducts')}`;
                    return innerX + innerWidth - emptyX > 120
                      ? <text x={emptyX + 5} y={innerY + 12} fill="#0369a1" fontSize="9" fontWeight="700">{label}</text>
                      : <text x={innerX + occupiedWidth - 5} y={innerY + 12} textAnchor="end" fill="white" fontSize="8" fontWeight="700">{label}</text>;
                  })() : null}
                  {!compact && occupiedWidth > 72 ? <text x={innerX + occupiedWidth / 2} y={innerY + innerHeight / 2 + 3} textAnchor="middle" fill="white" fontSize="8" fontWeight="700">{fillLabel} · {estimatedItems.length} {t('supply.supplyAgent.loadEstimateShortProducts')}</text> : null}
                  {innerWidth - occupiedWidth > 115 ? <text x={emptyX + (innerX + innerWidth - emptyX) / 2} y={innerY + innerHeight / 2 + 3} textAnchor="middle" fill="#475569" fontSize="8" fontWeight="600">{t('supply.supplyAgent.loadEstimateEmptySpace')}</text> : null}
                </g>
              );
            })() : null}
            {placements.length === 0 && previewItems.length > 0 && estimateOnly && estimateViewMode === 'pallets' ? (() => {
              const innerX = cargoX + 4;
              const innerY = cargoY + 5;
              const innerWidth = cargoWidth - 8;
              const innerHeight = cargoHeight - 10;
              const palletSlots = estimatePalletSlots.length > 0 ? estimatePalletSlots : estimatedItems.slice(0, 1).map((item) => ({ shares: [{ item, fraction: 1 }] }));
              // Escala real: un pallet EUR (1,2 m) ocupa su parte proporcional del largo del compartimento y,
              // a lo ancho del vehículo, caben varios (se ven en profundidad). Se cargan desde el fondo (cabina).
              const palletsAcross = Math.max(1, containerWidth ? Math.floor(containerWidth / 800) : 2);
              const palletBodyW = Math.max(8, Math.min((1200 / (containerLength || DEFAULT_ISO_DIMS.length)) * innerWidth, innerHeight - 8));
              const palletColumnsFit = Math.max(1, Math.floor(innerWidth / palletBodyW));
              const palletCount = Math.max(1, Math.min(palletSlots.length, palletColumnsFit * palletsAcross));
              const occupiedWidth = Math.ceil(palletCount / palletsAcross) * palletBodyW;
              const emptyX = innerX + occupiedWidth;
              const palletWidth = palletBodyW;
              const baseY = innerY + innerHeight - 12;
              const drawOrder = Array.from({ length: palletCount }, (_, index) => index).sort((a, b) => (b % palletsAcross) - (a % palletsAcross) || a - b);
              return (
                <g>
                  {occupiedWidth < innerWidth ? <rect x={emptyX} y={innerY} width={innerX + innerWidth - emptyX} height={innerHeight} fill="#ffffff" fillOpacity={transportMode === 'sea' ? 0.16 : 0.32} /> : null}
                  {drawOrder.map((palletIndex) => {
                    const slotShares = palletSlots[palletIndex % palletSlots.length].shares;
                    const item = slotShares[0].item;
                    const isMixedPallet = slotShares.length > 1 || slotShares[0].fraction < 0.995;
                    const isPalletSelected = slotShares.some((share) => share.item.productId === selectedProductId);
                    const palletColumn = Math.floor(palletIndex / palletsAcross);
                    const palletRow = palletIndex % palletsAcross;
                    const slotX = innerX + palletColumn * palletWidth + palletRow * 3;
                    const palletX = slotX + 1;
                    const palletW = Math.max(3, palletWidth - 2);
                    const frontSlot = toFrontSlot(palletSlots[palletIndex % palletSlots.length]);
                    const frontView = palletFrontView(frontSlot);
                    // Las cajas altas alargan el encuadre: si no caben en el compartimento, se reduce el pallet entero.
                    const graphicFit = Math.min(1, (innerHeight - 2) / (palletW * (170 / 150) * frontView.aspect));
                    const graphicW = palletW * (170 / 150) * graphicFit;
                    const graphicH = graphicW * frontView.aspect;
                    const baseYRow = baseY - palletRow * 4;
                    return (
                      <g
                        key={`pallet-preview-${item.id}-${palletIndex}`}
                        transform={`translate(${visualPalletOffsets[palletIndex]?.x || 0} ${visualPalletOffsets[palletIndex]?.y || 0})`}
                        opacity={selectedProductId == null || isPalletSelected ? 1 : 0.24}
                        role="group"
                        aria-label={`${slotShares.map((share) => share.item.label).join(' + ')} · pallet ilustrativo. Arrastrar para mover.`}
                        className={`sv-pop ${estimateOnly ? 'cursor-grab active:cursor-grabbing' : ''}`}
                        style={{ animationDelay: `${Math.min(palletIndex * 45, 900)}ms`, ...(estimateOnly ? { touchAction: 'none' } : {}) }}
                        onPointerDown={estimateOnly ? (event) => {
                          const svg = event.currentTarget.ownerSVGElement;
                          if (!svg) return;
                          const bounds = svg.getBoundingClientRect();
                          const origin = visualPalletOffsets[palletIndex] || { x: 0, y: 0 };
                          draggingVisualPallet.current = {
                            index: palletIndex,
                            pointerId: event.pointerId,
                            startX: event.clientX,
                            startY: event.clientY,
                            scaleX: 700 / bounds.width,
                            scaleY: 235 / bounds.height,
                            originX: origin.x,
                            originY: origin.y,
                            minX: cargoX + 2 - palletX,
                            maxX: cargoX + cargoWidth - 2 - (palletX + palletW),
                            minY: cargoY + 2 - (baseYRow + 3 - graphicH),
                            maxY: cargoY + cargoHeight - 9 - baseYRow,
                          };
                          event.currentTarget.setPointerCapture(event.pointerId);
                        } : undefined}
                        onPointerMove={estimateOnly ? (event) => {
                          const drag = draggingVisualPallet.current;
                          if (!drag || drag.index !== palletIndex || drag.pointerId !== event.pointerId) return;
                          const x = Math.max(drag.minX, Math.min(drag.maxX, drag.originX + (event.clientX - drag.startX) * drag.scaleX));
                          const y = Math.max(drag.minY, Math.min(drag.maxY, drag.originY + (event.clientY - drag.startY) * drag.scaleY));
                          setVisualPalletOffsets((current) => ({ ...current, [palletIndex]: { x, y } }));
                        } : undefined}
                        onPointerUp={estimateOnly ? () => { draggingVisualPallet.current = null; } : undefined}
                        onPointerCancel={estimateOnly ? () => { draggingVisualPallet.current = null; } : undefined}
                      >
                        <title>{isMixedPallet ? slotShares.map((share) => `${share.item.label} ${Math.round(share.fraction * 100)} %`).join(' · ') : `${item.label} · pallet ilustrativo`}</title>
                        <ellipse cx={palletX + palletW / 2} cy={baseYRow + 5} rx={palletW / 2} ry="3" fill="#0f172a" fillOpacity="0.18" />
                        <svg x={palletX + (palletW - graphicW) / 2} y={baseYRow + 3 - graphicH} width={graphicW} height={graphicH} viewBox={frontView.viewBox} overflow="visible" pointerEvents="none">
                          <PalletFrontGraphic
                            slot={frontSlot}
                            selectedIndex={null}
                          />
                        </svg>
                        {isPalletSelected ? <rect x={palletX - 2} y={baseYRow + 1 - graphicH} width={palletW + 4} height={graphicH + 4} rx="2" fill="none" stroke="#0f172a" strokeWidth="2" pointerEvents="none" /> : null}
                      </g>
                    );
                  })}
                  {palletSlots.length > palletCount ? <text x={innerX + innerWidth - 4} y={innerY + 8} textAnchor="end" fill="#475569" fontSize="6.5" fontWeight="700">+{t('supply.supplyAgent.loadIsoPallets', { count: palletSlots.length - palletCount })}</text> : null}
                </g>
              );
            })() : null}
            {placements.length === 0 && previewItems.length > 0 && (!estimateOnly || estimateViewMode === 'products') ? (() => {
              const gap = 2;
              const innerX = cargoX + 4;
              const innerY = cargoY + 5;
              const innerWidth = cargoWidth - 8;
              const innerHeight = cargoHeight - 10;
              const occupiedWidth = innerWidth * occupiedRatio;
              const emptyX = innerX + occupiedWidth;
              const rows = [previewItems.slice(0, Math.ceil(previewItems.length / 2)), previewItems.slice(Math.ceil(previewItems.length / 2))]
                .filter((row) => row.length > 0);
              return (
                <g>
                  {occupiedWidth < innerWidth ? (
                    <rect x={emptyX} y={innerY} width={innerX + innerWidth - emptyX} height={innerHeight} fill="#ffffff" fillOpacity={transportMode === 'sea' ? 0.16 : 0.32} />
                  ) : null}
                  {rows.flatMap((rowItems, rowIndex) => {
                    const rowY = innerY + rowIndex * (innerHeight / rows.length);
                    const rowHeight = innerHeight / rows.length - gap;
                    const rowTotal = rowItems.reduce((sum, item) => sum + item.estimatedVolume, 0);
                    let x = innerX;
                    return rowItems.map((item, index) => {
                      const width = index === rowItems.length - 1
                        ? innerX + occupiedWidth - x
                        : Math.max(1, (item.estimatedVolume / rowTotal) * occupiedWidth - gap);
                      const label = item.label.length > Math.floor(width / 5) ? `${item.label.slice(0, Math.max(4, Math.floor(width / 5) - 1))}…` : item.label;
                      const isSelectedTile = selectedProductId === item.productId;
                      const tile = (
                        <g
                          key={`${item.id}-${rowIndex}`}
                          className="sv-pop sv-tile"
                          style={{ animationDelay: `${Math.min(index * 40, 800)}ms` }}
                          opacity={selectedProductId == null || isSelectedTile ? 1 : 0.24}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isSelectedTile}
                          aria-label={item.label}
                          onClick={() => setSelectedProductId(isSelectedTile ? null : item.productId)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedProductId(isSelectedTile ? null : item.productId);
                            }
                          }}
                        >
                          <title>{`${item.label} · ${formatProductVolume(item.estimatedVolume)} m³`}</title>
                          <rect x={x} y={rowY} width={Math.max(0, width)} height={rowHeight} rx="2" fill={item.color} fillOpacity="0.94" stroke={isSelectedTile ? '#0f172a' : '#fff'} strokeWidth={isSelectedTile ? 2 : 1} />
                          {width > 36 ? <text x={x + width / 2} y={rowY + rowHeight / 2 + 2} textAnchor="middle" fill="white" fontSize="7" fontWeight="700">{label}</text> : null}
                        </g>
                      );
                      x += width + gap;
                      return tile;
                    });
                  })}
                </g>
              );
            })() : null}
            {!isLcl && transportMode === 'road' && placements.length > 0 ? (
              <g pointerEvents="none">
                <path d={`M ${cargoX + cargoWidth - 3} ${cargoY + 2} V ${cargoY + cargoHeight - 2}`} stroke="#0f172a" strokeOpacity="0.55" strokeWidth="1.2" strokeDasharray="3 2" />
                <text x={cargoX + cargoWidth - 6} y={cargoY + 9} textAnchor="end" fill="#334155" fontSize="6.5" fontWeight="700">{t('supply.supplyAgent.loadPlanDoors')} →</text>
                <text x={cargoX + 5} y={cargoY + 9} textAnchor="start" fill="#334155" fontSize="6.5" fontWeight="700">← {t('supply.supplyAgent.loadPlanCab')}</text>
              </g>
            ) : null}
            {placements.length === 0 && previewItems.length === 0 ? (
              <g>
                <rect x={cargoX + 8} y={cargoY + 8} width={cargoWidth - 16} height={cargoHeight - 16} rx="5" fill="#fff" fillOpacity="0.88" stroke="#cbd5e1" strokeDasharray="5 4" />
                <text x={cargoX + cargoWidth / 2} y={cargoY + cargoHeight / 2 - 2} textAnchor="middle" fill="#334155" fontSize="10" fontWeight="700">
                  {t('supply.supplyAgent.loadPlacementEmpty')}
                </text>
                <text x={cargoX + cargoWidth / 2} y={cargoY + cargoHeight / 2 + 11} textAnchor="middle" fill="#64748b" fontSize="7">
                  {t('supply.supplyAgent.loadStowageIncomplete')}
                </text>
              </g>
            ) : null}
          </g>
        </svg>}
        {estimateOnly && !compact && !showIso && placements.length === 0 && estimateViewMode === 'pallets' ? <PalletFrontGallery
          slots={estimatePalletSlots.map(toFrontSlot)}
          selectedIndex={selectedProductId != null ? displayIndexByProduct.get(selectedProductId) ?? null : null}
          t={t}
        /> : null}
      </div>

      {productsAction ? <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">{productsAction}</div> : null}

      {detailProducts.length > 0 ? (
        <details open={productsOpen} onToggle={(event) => setProductsOpen(event.currentTarget.open)} className="group border-t border-slate-100">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 bg-slate-50/70 px-4 py-3 text-xs marker:hidden hover:bg-slate-100">
            <span className="inline-flex items-center gap-2 font-semibold text-slate-800">
              <ChevronDown size={16} aria-hidden="true" className="shrink-0 text-sky-700 transition-transform group-open:rotate-180" />
              {t(estimateOnly ? 'supply.supplyAgent.loadEstimateProductsCount' : 'supply.supplyAgent.loadProductsCount', { count: detailProducts.length })}
            </span>
            {estimateOnly ? <span className="font-medium text-sky-700">{t('supply.supplyAgent.loadEstimateProductsShowDetails')}</span> : null}
          </summary>
          <div className="px-4 pb-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {estimateOnly ? <div role="group" aria-label={t('supply.supplyAgent.loadEstimateProductFilterLabel')} className="flex flex-wrap items-center gap-1 rounded-lg bg-slate-100 p-1">
              {([
                ['all', t('supply.supplyAgent.loadEstimateFilterAll', { count: rawItems.length })],
                ['included', t('supply.supplyAgent.loadEstimateFilterIncluded', { count: includedProductsCount })],
                ['missing', t('supply.supplyAgent.loadEstimateFilterMissing', { count: missingProductsCount })],
                ...(hasTargetColumn ? [['adjusted', t('supply.supplyAgent.loadEstimateFilterAdjusted', { count: targetAdjustedCount })]] as const : []),
              ] as const).map(([filter, label]) => <button key={filter} type="button" aria-pressed={effectiveDataFilter === filter} onClick={() => { setProductDataFilter(filter); setProductsPage(1); }} className={`rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${effectiveDataFilter === filter ? `bg-white shadow-sm ${filter === 'adjusted' ? 'text-emerald-800' : 'text-sky-800'}` : filter === 'adjusted' ? 'text-emerald-700 hover:text-emerald-900' : 'text-slate-600 hover:text-slate-900'}`}>
                {label}
              </button>)}
            </div> : <span className="text-xs font-medium text-slate-600">{t('supply.supplyAgent.loadProductsCount', { count: detailProducts.length })}</span>}
            {estimateOnly && missingProductsCount > 0 && (canOpenProductMaster || canOpenLogisticsMasters) ? <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
              {t('supply.supplyAgent.loadMissingFixHint')}
              {canOpenProductMaster ? <Link to={missingItems.length === 1 && missingItems[0].productId != null ? `/settings/products/${missingItems[0].productId}` : '/settings/products'} target="_blank" rel="noopener noreferrer" className="font-medium text-sky-700 hover:underline">{t('supply.supplyAgent.loadMissingOpenProduct')}</Link> : null}
              {canOpenLogisticsMasters ? <Link to={`/settings/logistics/masters${missingSupplierIds.length === 1 ? `?supplier=${missingSupplierIds[0]}` : ''}`} target="_blank" rel="noopener noreferrer" className="font-medium text-sky-700 hover:underline">{t('supply.supplyAgent.loadMissingOpenPallets')}</Link> : null}
            </span> : null}
            {estimateOnly && rawItems.some((item) => item.roundingStep != null) ? <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700">
              <input type="checkbox" checked={roundToFullBoxes} onChange={(event) => {
                const enabled = event.currentTarget.checked;
                if (!enabled && roundedBoxesApplied) {
                  onClearRoundedBoxes?.(rawItems.filter((item) => item.productId != null && item.caseRoundingExtraUnits > 0).map((item) => ({ productId: item.productId!, supplierId: item.supplierId })));
                  setRoundedBoxesApplied(false);
                }
                setRoundToFullBoxes(enabled);
                setProductsPage(1);
                updateExtraBoxes(() => ({}));
              }} className="h-3.5 w-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
              {t('supply.supplyAgent.loadEstimateRoundToFullBoxes')}
            </label> : null}
            <div className="ml-auto flex items-center gap-1.5">
              {estimateOnly ? <details className="relative">
                <summary className="flex cursor-pointer list-none items-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium text-slate-500 marker:hidden hover:bg-slate-100 hover:text-slate-700">
                  <Info size={13} aria-hidden="true" />
                  {t('supply.supplyAgent.loadInfoToggle')}
                </summary>
                <div className="absolute right-0 z-20 mt-1 w-80 space-y-2 rounded-lg border border-slate-200 bg-white p-3 text-[11px] leading-relaxed text-slate-600 shadow-lg">
                  <p>{t('supply.supplyAgent.loadEstimateProductsVolumeHelp')}</p>
                  <p>{t('supply.supplyAgent.loadEstimateConfiguredDataLabel')}</p>
                  <p>{t('supply.supplyAgent.loadEstimateBoxVolumeUnavailable')}</p>
                </div>
              </details> : null}
              {detailProducts.length > 6 ? <label className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1.5 text-xs text-slate-500"><Search size={13} aria-hidden="true" /><span className="sr-only">{t('supply.supplyAgent.loadSearchProducts')}</span><input value={search} onChange={(event) => { setSearch(event.target.value); setProductsPage(1); }} placeholder={t('supply.supplyAgent.loadSearchProducts')} className="w-36 bg-transparent outline-none placeholder:text-slate-400" /></label> : null}
            </div>
          </div>
          {estimateOnly && roundToFullBoxes && caseRoundingProductCount > 0 ? <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
            <p role="status" className="leading-relaxed">
              {t('supply.supplyAgent.loadEstimateRoundedBoxesNotice', { products: caseRoundingProductCount, units: formatLoadQuantity(caseRoundingUnitCount) })}
            </p>
            <div className="flex shrink-0 items-center gap-2">
              {roundedBoxesApplied ? <button type="button" onClick={() => {
                onClearRoundedBoxes?.(rawItems.filter((item) => item.productId != null && item.caseRoundingExtraUnits > 0).map((item) => ({ productId: item.productId!, supplierId: item.supplierId })));
                setRoundedBoxesApplied(false);
              }} className="rounded-md border border-amber-300 bg-white px-2.5 py-1.5 font-medium hover:bg-amber-100">
                {t('supply.supplyAgent.loadEstimateRestoreSuggested')}
              </button> : <button type="button" onClick={() => {
                onApplyRoundedBoxes?.(rawItems.filter((item) => item.productId != null && item.caseRoundingExtraUnits > 0).map((item) => ({
                  productId: item.productId!,
                  supplierId: item.supplierId,
                  quantity: item.roundedOrderUnits,
                })));
                setRoundedBoxesApplied(true);
              }} className="rounded-md bg-sky-700 px-2.5 py-1.5 font-semibold text-white hover:bg-sky-800">
                {t('supply.supplyAgent.loadEstimateApplyRounded')}
              </button>}
            </div>
          </div> : null}
          {estimateOnly && roundedBoxesApplied ? <div role="status" className="mb-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] leading-relaxed text-emerald-900">
            <p className="font-medium">{t('supply.supplyAgent.loadEstimateRoundedAppliedNotice', {
              products: caseRoundingProductCount,
              units: formatLoadQuantity(caseRoundingUnitCount),
            })}</p>
            <details className="mt-1">
              <summary className="w-fit cursor-pointer text-emerald-800 hover:underline">
                {t('supply.supplyAgent.loadEstimateRoundedAppliedDetails')}
              </summary>
              <ul className="mt-1 grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
                {rawItems.filter((item) => item.caseRoundingExtraUnits > 0).map((item) => <li key={item.id} className="flex min-w-0 justify-between gap-2">
                  <span className="truncate">{item.label}</span>
                  <span className="shrink-0">{t('supply.supplyAgent.loadEstimateRoundedAppliedLine', {
                    original: formatLoadQuantity(item.orderedUnits),
                    rounded: formatLoadQuantity(item.roundedOrderUnits),
                    added: formatLoadQuantity(item.caseRoundingExtraUnits),
                  })}</span>
                </li>)}
              </ul>
            </details>
          </div> : null}
          {estimateOnly && addedBoxCount > 0 ? <p role="status" className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-900">
            {t('supply.supplyAgent.loadEstimateExtraBoxWarning', {
              boxes: addedBoxCount,
              units: formatLoadQuantity(addedUnitCount),
            })}
          </p> : null}
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <div>
              {showColumnGroups ? <div style={productGridStyle} className="grid gap-x-2 bg-white px-2 pt-2 text-[10px] font-semibold uppercase tracking-wide">
                <span className="sticky left-0 z-[1] -ml-2 bg-white pl-2" />
                {productColumnGroups.map((group) => <span
                  key={group.key}
                  style={{ gridColumn: `span ${group.span} / span ${group.span}` }}
                  className={`border-b-2 pb-1 text-center ${group.className}`}
                >
                  {group.label}
                </span>)}
              </div> : null}
              <div style={productGridStyle} className="grid items-end gap-x-2 border-b border-slate-200 bg-slate-50 px-2 py-2 text-[10px] font-semibold uppercase leading-tight tracking-wide text-slate-500">
                <span className={estimateOnly ? 'sticky left-0 z-[1] -my-2 -ml-2 bg-slate-50 py-2 pl-2' : ''}>{t('supply.supplyAgent.loadProductsColumn')}</span>
                {productColumns.map((column) => <span key={column.key} className={`${column.align === 'left' ? 'text-left' : 'text-right'} ${column.key === 'target' ? 'text-emerald-700' : ''}`}>{column.label}</span>)}
              </div>
              <div className={expanded ? 'max-h-56 overflow-y-auto pr-1' : ''}>
                {pagedProducts.length === 0 ? <p className="px-3 py-6 text-center text-xs text-slate-500">{t('supply.supplyAgent.loadEstimateNoFilteredProducts')}</p> : null}
                {pagedProducts.map((item) => {
                  const isSelected = selectedProductId === item.productId;
                  const additionalBoxCount = item.extraBoxes;
                  const roundedBoxCount = item.boxCount == null ? null : Math.ceil(item.boxCount - 1e-9);
                  const maxExtraBoxes = maxExtraBoxesAllowed(item);
                  const context = item.productId != null ? productContext?.[item.productId] : undefined;
                  const targetAdjustment = item.productId != null ? targetByProduct[item.productId] : undefined;
                  const targetAccepted = item.productId != null && targetColumn?.accepted?.[item.productId] === true;
                  const targetDimmed = targetProposalPending && targetAdjustment != null && !targetAccepted;
                  const isMissing = estimateOnly && isMissingLogisticsItem(item);
                  const isLocked = item.productId != null && lockedProducts?.[item.productId] === true;
                  const rowBg = isSelected ? 'bg-sky-50' : targetAdjustment && !targetDimmed ? 'bg-emerald-50' : 'bg-white group-hover:bg-slate-50';
                  const numberCell = 'text-right tabular-nums text-slate-700';
                  const renderCell = (key: string) => {
                    switch (key) {
                      case 'units':
                        return <span className={numberCell}>
                          <span className="block font-medium text-slate-800">{formatLoadQuantity(item.roundedOrderUnits)}</span>
                          {item.caseRoundingExtraUnits > 0 ? <span className="block text-[10px] text-amber-700" title={t('supply.supplyAgent.loadEstimateRoundedFromUnits', { original: formatLoadQuantity(item.orderedUnits), added: formatLoadQuantity(item.caseRoundingExtraUnits) })}>+{formatLoadQuantity(item.caseRoundingExtraUnits)} {t('supply.supplyAgent.loadRoundedShort')}</span> : null}
                          {additionalBoxCount > 0 ? <span className="block text-[10px] font-medium text-amber-700">+{formatLoadQuantity(item.extraUnits)} {t('supply.supplyAgent.loadExtraShort')}</span> : null}
                        </span>;
                      case 'target':
                        return targetAdjustment ? <div className={`flex min-w-0 items-start gap-1.5 transition-opacity ${targetDimmed ? 'opacity-50' : ''}`}>
                          {targetProposalPending && item.productId != null ? <input
                            type="checkbox"
                            checked={targetAccepted}
                            onChange={(event) => targetColumn?.onToggle?.(item.productId!, event.currentTarget.checked)}
                            aria-label={t('supply.supplyAgent.loadTargetAcceptLine', { product: item.label })}
                            className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          /> : null}
                          <span className="min-w-0 tabular-nums">
                            <span className="flex flex-wrap items-center gap-1 text-slate-700">
                              <span className="whitespace-nowrap">{formatLoadQuantity(targetAdjustment.originalUnits, 0)} → <b className="text-slate-900">{formatLoadQuantity(targetAdjustment.newUnits, 0)}</b></span>
                              <UnitsDeltaChip delta={targetAdjustment.newUnits - targetAdjustment.originalUnits} />
                            </span>
                            {targetAdjustment.coverageBefore != null && targetAdjustment.coverageAfter != null
                              ? <span className="block text-[10px] text-slate-500">{t('supply.supplyAgent.loadTargetCoverageShort', { from: formatLoadQuantity(targetAdjustment.coverageBefore, 0), to: formatLoadQuantity(targetAdjustment.coverageAfter, 0) })}</span>
                              : null}
                            {targetColumn?.state === 'applied' ? <span className="block text-[10px] font-medium text-emerald-700">{t('supply.supplyAgent.loadTargetApplied')}</span> : null}
                          </span>
                        </div> : <span className="text-slate-300">—</span>;
                      case 'stock':
                        return <span className={numberCell}>
                          <span className="block">{formatContextQuantity(context?.stock)}</span>
                        </span>;
                      case 'inTransit':
                        return <span className={numberCell}>{context?.inTransit ? formatContextQuantity(context.inTransit) : <span className="text-slate-300">—</span>}</span>;
                      case 'boxesPerLayer':
                        return <span className={numberCell}>{item.boxesPerLayer != null && item.boxesPerLayer > 0 ? formatLoadQuantity(item.boxesPerLayer, 0) : <span className="text-slate-300">—</span>}</span>;
                      case 'layers':
                        return <span className={numberCell}>{item.layersPerPallet != null && item.layersPerPallet > 0 ? formatLoadQuantity(item.layersPerPallet, 0) : <span className="text-slate-300">—</span>}</span>;
                      case 'sales':
                        return <span className={numberCell}>{formatContextQuantity(context?.monthlySales)}</span>;
                      case 'salesEst':
                        return <span className={numberCell}>{formatContextQuantity(context?.estimatedMonthlySales)}</span>;
                      case 'coverageFuture':
                        return <span className="text-right tabular-nums text-slate-700">
                          {context?.coverageWithSuggestedDays == null || !Number.isFinite(context.coverageWithSuggestedDays)
                            ? <span className="text-slate-400">—</span>
                            : t('supply.supplyAgent.loadCoverageDays', { days: formatLoadQuantity(context.coverageWithSuggestedDays, 0) })}
                        </span>;
                      case 'abc':
                        return <span className="text-right font-medium text-slate-700">{context?.abcCategory || <span className="font-normal text-slate-400">—</span>}</span>;
                      case 'coverage':
                        return <span className="text-right tabular-nums">
                          {context?.coverageDays == null || !Number.isFinite(context.coverageDays)
                            ? <span className="text-slate-400">—</span>
                            : <span className={`inline-block rounded-full px-1.5 py-0.5 ${context.lowCoverage ? 'bg-amber-100 font-medium text-amber-800' : 'text-slate-700'}`}>
                              {t('supply.supplyAgent.loadCoverageDays', { days: formatLoadQuantity(context.coverageDays, 0) })}
                            </span>}
                        </span>;
                      case 'boxes':
                        if (item.isNewAssortment) return <span className="text-right text-slate-300">—</span>;
                        return <span
                          className={numberCell}
                          title={[
                            item.boxCount != null ? `${formatLoadQuantity(item.boxCount)} ${t('supply.supplyAgent.loadBoxEquivalentShort')}` : '',
                            item.unitsPerBox != null && item.unitsPerBox > 0 ? `${formatLoadQuantity(item.unitsPerBox, 0)} ${t('supply.supplyAgent.loadUnitsPerBoxShort')}` : '',
                          ].filter(Boolean).join(' · ') || undefined}
                        >
                          {roundedBoxCount == null
                            ? <span className="text-[10px] font-medium text-amber-700" title={t('supply.supplyAgent.loadPackagingMissing')}>{t('supply.supplyAgent.loadMissingShortBadge')}</span>
                            : formatLoadQuantity(roundedBoxCount)}
                        </span>;
                      case 'pallets':
                        return <span className={numberCell} title={[item.palletCount == null ? '' : t('supply.supplyAgent.loadPalletsRoundedTitle', { count: item.palletCount }), item.unitVolume == null ? '' : `${item.estimatedVolume.toLocaleString(undefined, { maximumFractionDigits: 3 })} m³`, item.estimatedWeight == null ? '' : `${formatLoadQuantity(item.estimatedWeight, 1)} ${weightUnit}`].filter(Boolean).join(' · ') || undefined}>
                          {item.palletEquivalent == null ? '—' : item.palletEquivalent > 0 && item.palletEquivalent < 0.01 ? `<${formatLoadQuantity(0.01)}` : formatLoadQuantity(item.palletEquivalent)}
                        </span>;
                      case 'extra':
                        return <div className="flex justify-end">
                          <input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            max={maxExtraBoxes}
                            step={1}
                            value={additionalBoxCount}
                            aria-label={t('supply.supplyAgent.loadExtraBoxesInput', { product: item.label })}
                            title={maxExtraBoxes > 0 ? t('supply.supplyAgent.loadExtraBoxesInputHelp', { max: maxExtraBoxes }) : t('supply.supplyAgent.loadAddBoxCapacityLimit')}
                            disabled={item.isNewAssortment || (maxExtraBoxes < 1 && additionalBoxCount < 1)}
                            onChange={(event) => {
                              const parsed = Number.parseInt(event.currentTarget.value, 10);
                              const nextValue = Number.isFinite(parsed) ? Math.max(0, Math.min(maxExtraBoxes, parsed)) : 0;
                              updateExtraBoxes((current) => ({ ...current, [item.id]: nextValue }));
                            }}
                            className="h-7 w-14 rounded border border-slate-200 bg-white px-1.5 text-center text-xs tabular-nums text-slate-800 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-400"
                          />
                        </div>;
                      default:
                        return null;
                    }
                  };
                  return <Fragment key={item.id}><div style={productGridStyle} className={`group grid items-center gap-x-2 border-b border-slate-100 px-2 py-2 text-xs last:border-b-0 ${rowBg}`}>
                    <div className={`flex min-w-0 items-center gap-1 self-stretch ${estimateOnly ? `sticky left-0 z-[1] -my-2 -ml-2 py-2 pl-2 shadow-[6px_0_6px_-6px_rgba(15,23,42,0.18)] ${rowBg}` : ''}`}>
                    <button type="button" title={item.label} aria-pressed={isSelected} onClick={() => {
                      const nextProductId = selectedProductId === item.productId ? null : item.productId;
                      setSelectedProductId(nextProductId);
                      // En la bandeja, elegir un producto abre la vista de pallets; en el módulo de estimación el camión
                      // se queda en el porcentaje lleno y solo se muestran las medidas del producto.
                      if (estimateOnly && !compact && nextProductId != null) {
                        setEstimateViewMode('pallets');
                      }
                    }} className="flex min-w-0 flex-1 items-center gap-2 self-stretch text-left">
                      <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-sm px-0.5 text-[9px] font-bold text-white" style={{ backgroundColor: item.color }}>{displayIndexByProduct.get(item.productId) || '·'}</span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 font-medium leading-4 text-slate-800">{item.label}</span>
                        <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1 text-[10px] text-slate-500">
                          {item.sku ? <span>{item.sku}</span> : null}
                          {showSupplier && item.supplierName ? <span className="truncate font-medium text-sky-700">· {item.supplierName}</span> : null}
                          {item.isNewAssortment ? <span className="rounded-full bg-emerald-100 px-1.5 font-semibold text-emerald-800">{t('supply.supplyAgent.loadNewAssortmentLabel')}</span> : null}
                          {isMissing ? <span title={t('supply.supplyAgent.loadEstimateProductMissingLogistics')} className="rounded-full bg-rose-100 px-1.5 font-semibold text-rose-800">{t('supply.supplyAgent.loadMissingShortBadge')}</span> : null}
                        </span>
                      </span>
                    </button>
                    {estimateOnly && onToggleLock && item.productId != null && !item.isNewAssortment ? <button
                      type="button"
                      aria-pressed={isLocked}
                      aria-label={t(isLocked ? 'supply.supplyAgent.loadUnlockLine' : 'supply.supplyAgent.loadLockLine', { product: item.label })}
                      title={t(isLocked ? 'supply.supplyAgent.loadUnlockLine' : 'supply.supplyAgent.loadLockLine', { product: item.label })}
                      onClick={() => onToggleLock(item.productId!, !isLocked)}
                      className={`shrink-0 rounded p-1 transition-colors ${isLocked ? 'bg-amber-100 text-amber-800' : 'text-slate-300 hover:bg-slate-100 hover:text-slate-600'}`}
                    >
                      {isLocked ? <Lock size={13} aria-hidden="true" /> : <Unlock size={13} aria-hidden="true" />}
                    </button> : null}
                    </div>
                    {productColumns.map((column) => <Fragment key={column.key}>{renderCell(column.key)}</Fragment>)}
                  </div>
                  {isSelected && estimateOnly ? <ProductConfigStrip
                    productId={item.productId}
                    supplierId={item.supplierId}
                    values={{ unitsPerBox: item.unitsPerBox, boxesPerLayer: item.boxesPerLayer, layers: item.layersPerPallet, unitsPerPallet: item.unitsPerPallet, unitVolume: item.unitVolume, unitWeight: item.unitWeight == null ? null : Number(item.unitWeight.toFixed(4)) }}
                    weightUnit={weightUnit}
                    weightFactor={weightFactor}
                    notStackable={item.stackable === false}
                    productLink={item.productId != null && canOpenProductMaster ? `/settings/products/${item.productId}` : null}
                    palletLink={canOpenLogisticsMasters ? `/settings/logistics/masters${item.supplierId ? `?supplier=${item.supplierId}` : ''}` : null}
                    onSave={canOpenLogisticsMasters ? onSaveProductConfig : undefined}
                    formatValue={(value) => formatLoadQuantity(value, value < 1 ? 4 : 2)}
                    t={t}
                  /> : null}
                  </Fragment>;
                })}
              </div>
            </div>
          </div>
          {estimateOnly ? <TablePaginationBar
            currentPage={safeProductsPage}
            totalPages={productsTotalPages}
            totalItems={filteredProducts.length}
            pageSize={productsPageSize}
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
            onPageChange={setProductsPage}
            onPageSizeChange={(size) => { setProductsPageSize(size); setProductsPage(1); }}
          /> : null}
          </div>
        </details>
      ) : null}

      {!estimateOnly && (stowagePlan?.unplaced_products?.length || remainingMissingData.length) ? <details className="border-t border-amber-100 bg-amber-50/60">
        <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 px-4 py-3 text-xs font-semibold text-amber-900 marker:hidden">
          <span>{t('supply.supplyAgent.loadUnplacedTitle')} · {(stowagePlan?.unplaced_products?.length || 0) + remainingMissingData.length}</span>
          <span className="font-normal text-amber-800">{t('supply.supplyAgent.loadUnplacedExpand')}</span>
        </summary>
        <div className="px-4 pb-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] leading-relaxed text-amber-900">{t('supply.supplyAgent.loadUnplacedHint')}</p>
            {(stowagePlan?.unplaced_products?.length || 0) > 6 ? <label className="flex shrink-0 items-center gap-1.5 rounded-md border border-amber-200 bg-white px-2 py-1 text-xs text-slate-500"><Search size={13} aria-hidden="true" /><span className="sr-only">{t('supply.supplyAgent.loadSearchUnplaced')}</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('supply.supplyAgent.loadSearchUnplaced')} className="w-36 bg-transparent outline-none placeholder:text-slate-400" /></label> : null}
          </div>
          <div className={`grid gap-2 sm:grid-cols-2 ${expanded ? 'max-h-64 overflow-y-auto pr-1' : 'max-h-48 overflow-y-auto pr-1'}`}>
          {filteredUnplacedProducts.map((item, index) => <div key={`${String(item.product_id || item.sku || index)}`} className="rounded-lg border border-amber-100 bg-white px-3 py-2 text-xs"><p className="font-medium text-slate-800">{String(item.product_name || item.sku || item.product_id || '')}</p><p className="mt-0.5 text-amber-800">{String(item.reason || '')}{item.requested_pallets != null ? ` · ${String(item.placed_pallets || 0)}/${String(item.requested_pallets)} pallets` : ''}</p></div>)}
          {filteredMissingData.map((item) => {
            const product = rawItems.find((line) => line.productId === item.product_id);
            const needsProductCard = item.fields.some((field) => PRODUCT_CARD_FIELDS.has(field));
            const needsPalletData = item.fields.some((field) => !PRODUCT_CARD_FIELDS.has(field));
            const cardLink = needsProductCard && item.product_id && canOpenProductMaster ? `/settings/products/${item.product_id}` : null;
            const palletLink = needsPalletData && canOpenLogisticsMasters
              ? `/settings/logistics/masters${product?.supplierId ? `?supplier=${product.supplierId}` : ''}`
              : null;
            return (
              <div key={`missing-${item.product_id}`} className="rounded-lg border border-amber-100 bg-white px-3 py-2 text-xs">
                <p className="font-medium text-slate-800">{product?.label || item.product_id}</p>
                <p className="mt-0.5 text-amber-800">{item.fields.join(', ')}</p>
                {cardLink || palletLink ? (
                  <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    {cardLink ? <Link to={cardLink} target="_blank" rel="noopener noreferrer" className="font-medium text-sky-700 hover:underline">{t('supply.supplyAgent.loadMissingOpenProduct')}</Link> : null}
                    {palletLink ? <Link to={palletLink} target="_blank" rel="noopener noreferrer" className="font-medium text-sky-700 hover:underline">{t('supply.supplyAgent.loadMissingOpenPallets')}</Link> : null}
                  </p>
                ) : null}
              </div>
            );
          })}
          </div>
        </div>
      </details> : null}


      {compact && hideMetrics ? null : (
      <>
      <div className={estimateOnly ? 'mx-4 my-3 grid divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 md:grid-flow-col md:auto-cols-fr md:divide-x md:divide-y-0' : 'grid gap-3 border-t border-slate-100 px-4 py-4 sm:grid-cols-2 lg:grid-cols-3'}>
        <div className={metricCardClass('border-sky-100 bg-sky-50/40', 'volume')}>{limitingPill('volume')}<MetricBar label={t('supply.supplyAgent.loadMetricProducts')} value={volumeFill} unit="%" minimum={estimateOnly ? null : minimumFill} maximum={estimateOnly ? null : maximumFill} barClass={metricBarClass('volume', 'bg-gradient-to-r from-sky-400 to-sky-600')} /><p className="mt-2 text-[11px] leading-relaxed text-slate-600">{t('supply.supplyAgent.loadProductVolumeHint', {
          used: displayedVolume == null ? '—' : displayedVolume.toLocaleString(undefined, { maximumFractionDigits: 1 }),
          capacity: usableVolume == null ? '—' : usableVolume.toLocaleString(undefined, { maximumFractionDigits: 1 }),
          fill: volumeFill == null ? '—' : volumeFill.toLocaleString(undefined, { maximumFractionDigits: 1 }),
          free: volumeFill == null ? '—' : Math.max(0, 100 - volumeFill).toLocaleString(undefined, { maximumFractionDigits: 1 }),
        })}</p></div>
        {!estimateOnly && palletEnvelopeFill != null && placements.length > 0 ? <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3"><MetricBar label={t('supply.supplyAgent.loadMetricPallets')} value={palletEnvelopeFill} unit="%" barClass="bg-gradient-to-r from-indigo-400 to-indigo-600" /><p className="mt-2 text-[11px] leading-relaxed text-slate-600">{t('supply.supplyAgent.loadPalletEnvelopeMetricHint', {
          used: palletEnvelopeVolume.toLocaleString(undefined, { maximumFractionDigits: 1 }),
          capacity: containerGeometricVolume?.toLocaleString(undefined, { maximumFractionDigits: 1 }),
        })}</p></div> : null}
        {palletFill != null ? <div className={metricCardClass('border-indigo-100 bg-indigo-50/40', 'pallets')}>{limitingPill('pallets')}<MetricBar label={t('supply.supplyAgent.loadMetricPalletsPositions')} value={palletFill} unit="%" barClass={metricBarClass('pallets', 'bg-gradient-to-r from-indigo-400 to-indigo-600')} /><p className="mt-2 text-[11px] leading-relaxed text-slate-600">{t('supply.supplyAgent.loadPalletsHint', { used: palletsNeeded, capacity: palletPositions })}</p></div> : null}
        <div className={metricCardClass('border-teal-100 bg-teal-50/40', 'weight')}>{limitingPill('weight')}<MetricBar label={t('supply.supplyAgent.loadMetricWeight')} value={weightFill} unit="%" barClass={metricBarClass('weight', 'bg-gradient-to-r from-teal-400 to-teal-600')} /><p className="mt-2 text-[11px] leading-relaxed text-slate-600">{t('supply.supplyAgent.loadWeightHint')}</p></div>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 border-t border-slate-100 bg-slate-50 px-4 py-2.5 text-xs text-slate-600">
        <span>{t('supply.supplyAgent.loadVolumeCapacity', { used: displayedVolume == null ? '—' : displayedVolume.toLocaleString(undefined, { maximumFractionDigits: 1 }), capacity: usableVolume == null ? '—' : usableVolume.toLocaleString(undefined, { maximumFractionDigits: 1 }) })}</span>
        {containerLength && containerWidth && containerHeight ? <span>{t('supply.supplyAgent.loadContainerDimensions', { length: (containerLength / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), width: (containerWidth / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), height: (containerHeight / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}</span> : null}
        <span>{t('supply.supplyAgent.loadWeightCapacity', { unit: weightUnit, used: displayedWeight == null ? '—' : displayedWeight.toLocaleString(undefined, { maximumFractionDigits: 0 }), capacity: usableWeight == null ? '—' : usableWeight.toLocaleString(undefined, { maximumFractionDigits: 0 }) })}</span>
        {limiting ? (
          <span className="font-semibold text-slate-800">
            {t('supply.supplyAgent.loadLimitingLabel', {
              name: t(limiting[0] === 'volume' ? 'supply.supplyAgent.loadLimitingVolume' : limiting[0] === 'weight' ? 'supply.supplyAgent.loadLimitingWeight' : 'supply.supplyAgent.loadLimitingPallets'),
              value: limiting[1].toLocaleString(undefined, { maximumFractionDigits: 1 }),
            })}
          </span>
        ) : null}
        {!estimateOnly && (minimumFill != null || maximumFill != null) ? (
          <span>{t('supply.supplyAgent.loadFillRange', { min: minimumFill ?? '—', max: maximumFill ?? '—' })}</span>
        ) : null}
      </div>
      </>
      )}

      {compact ? null : <p className="border-t border-slate-100 px-4 py-2.5 text-xs leading-relaxed text-slate-500">
        {estimateOnly
          ? t('supply.supplyAgent.loadEstimateDisclaimer')
          : stowagePlan?.warnings?.length
          ? stowagePlan.warnings.join(' ')
          : stowagePlan
            ? t('supply.supplyAgent.loadVolumeDisclaimer')
            : t('supply.supplyAgent.loadUnitsDisclaimer')}
      </p>}
    </section>
  );
}
